import { camelizeKeys, decamelizeKeys } from 'humps'
import { UseMutateProps } from 'restful-react'

interface IAnyUseMutateProps extends UseMutateProps<any, any, any, any, any> {}

interface IGetRestfulPropsReturn extends Pick<IAnyUseMutateProps, 'requestOptions' | 'resolve'> {}

/**
 * Returns object with `requestOptions` and `resolve` settings.
 *
 * These props enable restful-react implementations for hooks/types that
 * aren't auto generated, but have APIs that expect snake_case property key names.
 *
 * @example
 * ```
 * const useGetTrucks = (props: IUseGetTrucksProps) => {
 *   return useGet(ENDPOINTS.trucks, {
 *     base: __API__,
 *     ...getRestfulProps(),
 *     ...props
 *   })
 * }
 * ```
 */
export const getRestfulProps = (): IGetRestfulPropsReturn => {
  return {
    /**
     * De-camelizes keys of props before sending request to backend.
     * This enables us to utilize Restful React with types written in
     * camel case, while not needing to change the case setting on our backend
     * services.
     *
     * We camelize keys in a similar way with `callApi`
     */
    requestOptions: (_url, _method, requestBody) => ({
      body: new Blob([JSON.stringify(decamelizeKeys({ ...requestBody }))], {
        type: 'text/plain',
      }),
    }),
    /**
     * This camelizes keys for usage on the front end, like we do in `callApi`
     */
    resolve: res => camelizeKeys(res) as any,
  }
}
